import { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogIn, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { SiFacebook, SiX, SiLinkedin, SiInstagram } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const { user } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { label: 'Home', path: ROUTE_PATHS.HOME },
    { label: 'Services', path: ROUTE_PATHS.SERVICES },
    { label: 'Pricing', path: ROUTE_PATHS.PRICING },
    { label: 'About', path: ROUTE_PATHS.ABOUT },
    { label: 'Contact', path: ROUTE_PATHS.CONTACT },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          scrolled
            ? 'glass-effect shadow-lg'
            : 'bg-background/60 backdrop-blur-sm'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <span className="text-white font-bold text-sm">ESA</span>
              </div>
              <div>
                <span className="text-xl font-semibold text-foreground">Elevation Systems AI</span>
                <p className="text-[10px] text-muted-foreground -mt-1">Credit To Millions</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              {navItems.slice(0, 5).map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-primary ${
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-muted-foreground hover:text-primary"
                onClick={() => navigate(ROUTE_PATHS.DASHBOARD)}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </Button>
              {user ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 border-primary/40 text-primary hover:bg-primary/10"
                  onClick={() => navigate(ROUTE_PATHS.ADMIN)}
                >
                  <LogIn className="w-4 h-4" />
                  Admin
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-muted-foreground hover:text-primary"
                  onClick={() => navigate(ROUTE_PATHS.LOGIN)}
                >
                  <LogIn className="w-4 h-4" />
                  Login
                </Button>
              )}
              <Button asChild variant="default" size="default">
                <Link to={ROUTE_PATHS.CONTACT}>Get Started</Link>
              </Button>
            </div>

            <button
              className="md:hidden p-2 text-foreground hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden border-t border-border bg-background"
            >
              <nav className="container mx-auto px-4 py-6 flex flex-col space-y-4">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `text-base font-medium transition-colors hover:text-primary ${
                        isActive ? 'text-primary' : 'text-muted-foreground'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Button
                  variant="outline"
                  size="default"
                  className="w-full gap-2"
                  onClick={() => { setMobileMenuOpen(false); navigate(ROUTE_PATHS.DASHBOARD); }}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  className="w-full gap-2"
                  onClick={() => { setMobileMenuOpen(false); navigate(user ? ROUTE_PATHS.ADMIN : ROUTE_PATHS.LOGIN); }}
                >
                  <LogIn className="w-4 h-4" />
                  {user ? 'Admin Portal' : 'Login'}
                </Button>
                <Button asChild variant="default" size="default" className="w-full">
                  <Link to={ROUTE_PATHS.CONTACT} onClick={() => setMobileMenuOpen(false)}>
                    Get Started
                  </Link>
                </Button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <span className="text-white font-bold text-sm">ESA</span>
                </div>
                <div>
                  <span className="text-xl font-semibold text-foreground">Elevation Systems AI</span>
                  <p className="text-[10px] text-muted-foreground -mt-1">Credit To Millions</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                elevationsystemsai.com — Your complete financial transformation partner. Credit repair, tax optimization, and business funding powered by AI.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Facebook"
                >
                  <SiFacebook size={20} />
                </a>
                <a
                  href="https://x.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="X (Twitter)"
                >
                  <SiX size={20} />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="LinkedIn"
                >
                  <SiLinkedin size={20} />
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Instagram"
                >
                  <SiInstagram size={20} />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Services</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to={ROUTE_PATHS.SERVICES}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Credit Repair
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.SERVICES}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Tax Services
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.SERVICES}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Business Consulting
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to={ROUTE_PATHS.ABOUT}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Contact</h3>
              <ul className="space-y-2">
                <li className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Phone:</span> (800) 274-6455
                </li>
                <li className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Email:</span> info@elevationsystemsai.com
                </li>
                <li className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Hours:</span> Mon-Fri 9AM-6PM
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-sm text-muted-foreground">
              © 2026 Elevation Systems AI | Credit To Millions. All rights reserved.
              </p>
              <div className="flex space-x-6">
                <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Privacy
                </a>
                <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Terms
                </a>
                <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Cookies
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}